import { z } from 'zod';
import { freeLabelSchema } from '../free-label.js';
import { uuidV7Schema } from '../uuid.js';
import { buildingTypeSchema } from './building-type.js';
import { appointmentContactLinksSchema } from './contacts.js';
import { inquirySchema } from './inquiry.js';
import { sewerNetworkTypeSchema } from './sewer-network-type.js';
/**
 * Ce que le back-office fournit pour **créer une demande de contrôle** : ses deux étiquettes, et
 * l'adresse où l'agent devra se rendre. Rien d'autre, parce que rien d'autre n'a de valeur à ce
 * moment-là.
 *
 * ⚠️ **Ni `status` ni `teamId`, et ce sont deux absences différentes.** Le statut est dérivé
 * côté serveur — une demande naît `received` (M3) —, comme `assignInquirySchema` laisse dériver
 * celui de l'affectation : le laisser au client ferait deux sources pour la même règle du cycle
 * de vie. L'équipe, elle, est un **autre geste**, déjà écrit (`POST /inquiries/:id/assign`) avec
 * sa marche `received → assigned` et son verrou ; l'accepter ici dupliquerait cette transition,
 * et la première divergence serait silencieuse.
 *
 * ⚠️ **Ni `siteId` ni `addressId` non plus : ils n'existent pas encore.** La demande, son site et
 * son adresse naissent ensemble — une demande sans adresse n'est pas une demande, et l'adresse
 * est ce que l'agent lit sur sa tablette. Demander un `siteId` obligerait l'écran à créer un site
 * d'abord, donc à tenir seul une transaction que le serveur tient déjà.
 *
 * ⚠️ **Les deux références sont libres, nullables et sans unicité** (M1) : pas de format imposé,
 * pas de `UNIQUE`. Une référence déjà prise est **signalée, pas refusée** — voir
 * `createdInquirySchema`. Le legacy en produit des doublons attestés (6 689 valeurs distinctes sur
 * 6 839 lignes), et une garde ici ferait échouer leur reprise sans rien réparer.
 *
 * ⚠️ **Une « Réf. GHP » absente n'est plus laissée vide** (23/09/2026) : le serveur en attribue une
 * (`GHP-2026-0001`, `reference.ts`). Une référence **fournie** passe telle quelle — c'est ce qui
 * garde la reprise et l'import intacts. La réf. **dossier**, elle, reste vide quand elle manque :
 * c'est celle du client, et personne d'autre que lui ne peut la donner.
 *
 * ⚠️ `inseeCode` est la seule valeur **exigée**, et c'est la première des trois exceptions de M21
 * — l'intégrité structurelle. `Address.insee_code` est `NOT NULL` et référence un référentiel
 * administré par l'État (M8) : il n'y a pas de commune de repli plausible, et en inventer une
 * placerait le contrôle dans la mauvaise ville.
 *
 * `strictObject` : un `status` ou un `teamId` de trop se rejette au lieu de se retirer en
 * silence — l'appelant croirait l'avoir imposé.
 */
/**
 * Une personne telle qu'un formulaire la rend — le demandeur, le propriétaire, le contact du
 * rendez-vous ([M15](../../../../docs/MODELE-DONNEES.md#m15)).
 *
 * ⚠️ **Aucun champ n'est exigé, pas même le nom** : un téléphone noté sans nom se garde, au bureau
 * comme sur la tablette (M24, #268, #379). Une personne morale n'a pas de prénom — le
 * propriétaire est souvent une SCI, le contact une agence —, et téléphone comme e-mail manquent
 * régulièrement des formulaires clients.
 *
 * ⚠️ **Le bloc entier vaut `null` quand rien n'y est écrit** — absent, `null`, ou tout en blanc
 * (`orNobody`) : une personne vide ne naît pas en base. `nullish` plutôt que `nullable` pour la
 * même raison que les étiquettes : une clé absente ne doit pas valoir `400`.
 */
const personFieldsSchema = z.strictObject({
    lastName: freeLabelSchema,
    firstName: freeLabelSchema,
    /**
     * ⚠️ **Enregistré tel qu'il est lu, même hors format** (M21). Il vient le plus souvent du PDF
     * d'un portail client (« tel uniquement » est une valeur réelle), et la boîte de création n'a
     * pas de champ pour le corriger : le refuser bloquait la demande entière, demandeur et
     * propriétaire compris. L'envoi, lui, ne part pas vers une adresse hors format
     * (`usableEmail`), et l'écran peut le dire (`emailWarning`).
     */
    email: freeLabelSchema,
    phone: freeLabelSchema,
});
function orNobody(person) {
    if (person === null || person === undefined)
        return null;
    return Object.values(person).some((field) => field != null) ? person : null;
}
const createPersonSchema = personFieldsSchema.nullish().transform(orNobody);
/**
 * Le **propriétaire**, seul à porter une adresse et une commune : le rapport papier les imprime
 * dans son bloc (#350). Facultatives jusque dans le type de sortie, comme `postalCode` : un
 * formulaire qui ne les propose pas n'a pas à les envoyer.
 */
const createOwnerSchema = personFieldsSchema
    .extend({ address: freeLabelSchema.optional(), municipality: freeLabelSchema.optional() })
    .nullish()
    .transform(orNobody);
export const createInquirySchema = z.strictObject({
    reference: freeLabelSchema,
    caseReference: freeLabelSchema,
    /** Le lien vers le dossier client (#410), facultatif — texte libre, `safeExternalUrl` en décide. */
    caseUrl: freeLabelSchema,
    /**
     * Le **type de contrôle** — « Vente », « SYAM », « Avant travaux »…
     *
     * ⚠️ **Champ libre, pas une énumération**, comme la colonne qu'il alimente (M2, M13) : le
     * vocabulaire vient du client, il s'imprime sur son rapport, et il a déjà changé une fois.
     * `INQUIRY_PURPOSES` en porte les six valeurs suggérées, dont l'écran fait une liste — une
     * garde ici refuserait la septième valeur que GHP ajoutera sans nous.
     */
    purpose: freeLabelSchema,
    /**
     * La **parcelle** du lieu à contrôler, telle qu'elle est écrite sur le document.
     *
     * ⚠️ **Elle se stocke, elle ne se dérive pas.** Le prototype lit `d.parcelle || fmtParcelle(ref)`
     * (`ghp-bo/editor.js:93`) : la valeur saisie l'emporte, la dérivation depuis la « Réf. GHP »
     * n'est qu'un repli d'affichage. Elle part sur le `Site`, avec l'adresse.
     */
    cadastralReference: freeLabelSchema,
    /**
     * Le **réseau public du dossier** — unitaire ou séparatif, choisi au bureau ou lu dans la colonne
     * « Unitaire ? » d'une liste (M43). Il part sur le `Site`, avec la parcelle.
     *
     * ⚠️ **Une liste fermée, et `null` quand on ne sait pas** : absent, il vaut `null`, jamais un
     * séparatif par défaut — le prototype en affiche un, la tablette annoncerait un réseau que
     * personne n'a dit. Ce n'est pas le réseau constaté sur place, qui reste dans le relevé.
     */
    sewerNetworkType: sewerNetworkTypeSchema.nullish().transform((type) => type ?? null),
    /**
     * Le **client donneur d'ordre**, choisi dans le référentiel — pas saisi au clavier.
     *
     * ⚠️ **Un identifiant, pas un nom.** Le prototype en fait une zone de texte parce que c'est une
     * maquette ; ici la colonne est une clé étrangère (M32), et accepter un nom obligerait le
     * serveur à deviner de quel client il s'agit — « Nantes Métropole » et « Nantes Métropole
     * Habitat » se ressemblent assez pour que la demande parte au mauvais.
     */
    clientId: uuidV7Schema.nullish().transform((id) => id ?? null),
    /**
     * La demande dont celle-ci est la **contre-visite** (#321, Q5), `null` pour un premier contrôle.
     * Le serveur en tire le lieu (le même `site`), la première demande de la chaîne et le rang.
     */
    followUpOfId: uuidV7Schema.nullish().transform((id) => id ?? null),
    /**
     * Le **lot** du lieu — celui d'un client, choisi dans ses lots en cours (#419). Il part sur le
     * `Site`, comme la parcelle : le lot est porté par le lieu (M38). `null` sans lot.
     */
    lotId: uuidV7Schema.nullish().transform((id) => id ?? null),
    address: z.strictObject({
        inseeCode: z.string().min(1),
        /**
         * Le **numéro de voie**, séparé de la rue.
         *
         * ⚠️ **Rien n'est redécoupé derrière.** La colonne existe depuis M32, mais `street` garde la
         * forme d'aujourd'hui — « 12 rue des Ormes » d'un seul tenant pour tout ce qui vient de
         * l'existant. Découper une valeur libre après coup fabriquerait des numéros que personne n'a
         * écrits (M1) ; ici, c'est la saisie qui les sépare, donc la séparation est constatée.
         */
        streetNumber: freeLabelSchema,
        // ⚠️ **Le numéro et la rue ensemble** restent possibles dans ce champ unique
        // (« 12 rue des Ormes ») : c'est la forme de l'existant, qui ne les sépare pas. Le champ
        // au-dessus sert la saisie qui, elle, les distingue.
        street: freeLabelSchema,
        /**
         * Le **code postal de l'adresse**, tel qu'écrit — absent, c'est celui de la commune qui
         * s'affiche. Une commune nouvelle n'a qu'un code au référentiel quand ses communes déléguées
         * gardent le leur (Montjean-sur-Loire en 49570, Mauges-sur-Loire en 49110) : c'est celui de la
         * voie qui fait l'adresse postale. Il ne choisit pas la commune — le code INSEE s'en charge.
         *
         * ⚠️ **Facultatif jusque dans le type de sortie** (`.optional()` hors de la transformation) :
         * un formulaire qui ne le propose pas n'a pas à l'envoyer, et absent vaut `null`.
         */
        postalCode: freeLabelSchema.optional(),
        /** La **commune déléguée**, telle qu'écrite (« Montjean-sur-Loire »), `null` sans elle. */
        delegatedName: freeLabelSchema.optional(),
    }),
    /**
     * Les trois personnes que le formulaire d'un portail client porte, et que [M15](#m15) place
     * chacune sur son entité : le demandeur et le contact du rendez-vous sur la demande, le
     * propriétaire sur le lieu.
     *
     * ⚠️ **Elles arrivent avec la demande ou elles se perdent.** Le document qui les portait n'est
     * conservé nulle part (D7) : ce qui n'est pas repris ici n'est nulle part, et il faudra
     * rappeler l'usager pour l'obtenir. C'est la raison pour laquelle la création les accepte, et
     * non le confort d'un champ de plus.
     *
     * ⚠️ **Facultatives toutes les trois** : une demande saisie à la main n'en a aucune, et les
     * exiger reviendrait à refuser la saisie manuelle (M21).
     */
    applicant: createPersonSchema,
    owner: createOwnerSchema,
    appointmentContact: createPersonSchema,
    /**
     * « En qualité de » — propriétaire, locataire, mandataire, gardien.
     *
     * ⚠️ **Elle qualifie le lien, pas la personne** (M32) : la même personne est propriétaire ici et
     * locataire ailleurs. D'où sa place sur la demande, à côté du contact, et non dans
     * `appointmentContact` — qui est une `Person`, partagée entre plusieurs demandes.
     *
     * ⚠️ **Champ libre** : le prototype en fait une zone de saisie, pas une liste.
     */
    appointmentContactCapacity: freeLabelSchema,
    /**
     * Les rôles que la personne présente tient **aussi** — les cases « Même personne que le
     * demandeur / le propriétaire » (#407). Cochée, une seule personne naît, avec les coordonnées de
     * `appointmentContact`, et la demande la lie aux deux rôles.
     *
     * ⚠️ **Le formulaire montre une personne, il en écrit une** : `appointmentContact` porte alors
     * ce que l'écran affiche, et l'emporte sur le bloc du rôle lié. Seules l'adresse et la commune
     * du propriétaire, que la personne présente ne porte pas, sont reprises de `owner` (#350).
     */
    appointmentContactSameAs: appointmentContactLinksSchema,
    /** La note pour l'équipe (#433), lue sur un PDF (#585) ; vide, elle ne s'écrit pas. */
    comment: freeLabelSchema,
    /**
     * La date de la demande (#435), lue sur le PDF ou saisie : elle s'affiche et s'exporte. ⚠️ Les
     * délais réglementaires partent de la création, pas d'elle (choix du 15/10).
     */
    requestedOn: z.iso
        .date()
        .nullish()
        .transform((day) => day ?? null),
    /** Le type de bâti du lieu (#586) — lu sur le PDF ou choisi ; facultatif. */
    buildingType: buildingTypeSchema.nullish().transform((type) => type ?? null),
});
/**
 * Ce que la création rend : la demande écrite, **et ce qu'il faut en dire**.
 *
 * ⚠️ **`duplicateReferences` est la raison d'être de cette enveloppe.** M1 refuse l'unicité sur
 * `reference` et le garde-fou n°3 refuse le blocage : reste la troisième branche, « on
 * enregistre, on **signale**, l'humain tranche ». Un avertissement qui ne vivrait que dans le
 * journal du serveur ne signalerait rien à personne — le gestionnaire qui vient de saisir la
 * référence est le seul à savoir si ce doublon est une reprise légitime (contre-visite, dossier
 * scindé) ou sa propre faute de frappe.
 *
 * C'est un **compte, pas un booléen** : « 2 autres demandes portent déjà cette référence » se
 * tranche, « une autre demande porte déjà cette référence » sous-entend qu'il n'y en a qu'une.
 * `0` est le cas courant, et il ne fait rien afficher.
 *
 * ⚠️ Il vaut `0` quand la référence a été **attribuée** par le serveur : elle prend le rang suivant
 * le plus haut de l'année, donc aucune autre demande ne la porte.
 */
export const createdInquirySchema = z.object({
    inquiry: inquirySchema,
    duplicateReferences: z.number().int().min(0),
});
/**
 * Ce que le bureau corrige sur une demande déjà créée : sa « Réf. GHP » (#406) et le lien vers son
 * dossier client (#410). **Un champ absent reste tel qu'il est.** La réponse est l'enveloppe de la
 * création — la demande, et combien d'**autres** demandes portent déjà la référence : un doublon se
 * signale, il ne se refuse pas (M1).
 */
export const updateInquirySchema = z.strictObject({
    reference: freeLabelSchema.optional(),
    caseUrl: freeLabelSchema.optional(),
    /**
     * Rattache à la main une demande à la demande dont elle est la **contre-visite** (#321) — celle
     * arrivée par un import, sans lien. Écrit une fois : un second rattachement est refusé (`409`).
     */
    followUpOfId: uuidV7Schema.optional(),
    /**
     * La même chose par la « Réf. GHP » du contrôle d'origine, telle que le bureau la tape. Le
     * serveur la cherche telle quelle : aucune demande, `404` ; plusieurs (M1), `409`.
     */
    followUpOfReference: z.string().trim().min(1).optional(),
    /**
     * Ce que « Modifier la demande » corrige après la création (#434) — un champ absent reste tel
     * qu'il est. L'adresse se donne entière, comme à la création ; partagée par une autre demande du
     * même lieu, elle ne se réécrit pas pour elle (M15).
     */
    caseReference: freeLabelSchema.optional(),
    purpose: freeLabelSchema.optional(),
    clientId: uuidV7Schema.nullable().optional(),
    cadastralReference: freeLabelSchema.optional(),
    /** Le type de bâti du lieu (#586) ; un lieu partagé reçoit sa copie, comme l'adresse. */
    buildingType: buildingTypeSchema.nullable().optional(),
    /** Le réseau du dossier (M43) ; `null` le rend inconnu. */
    sewerNetworkType: sewerNetworkTypeSchema.nullable().optional(),
    address: createInquirySchema.shape.address.optional(),
    requestedOn: z.iso.date().nullable().optional(),
    /** La note pour l'équipe (#433), vue au planning et sur la tablette ; vide, elle s'efface. */
    comment: freeLabelSchema.optional(),
    /** La case « Urgent » (Sentry BACK-OFFICE-X), dite en rouge au bureau et sur la tablette. */
    urgent: z.boolean().optional(),
});
//# sourceMappingURL=creation.js.map